import { CLASS_NAME } from 'constants/class-names'
import { Nullable } from 'constants/types'
import { AriaAttr, EventType, HtmlAttr, KeyType } from 'constants/enums'
import { ATTR_NAME } from 'constants/attr-name'
import {
    isMobile,
    reduceMotionQueryList,
    isReduceMotion
} from 'utils/system/device'
import { ComponentBase } from 'utils/abstract-classes/component-base'

import { AnimationName } from 'components/header/header-interface'
import { ArrowDirection, MenuItem } from 'components/header/menu-item'
import { HeaderStore } from 'components/header/store'
import { CarComparisonStore } from 'components/driving/store'

const {
    header: { search: searchClass, menu: menuClass },
    common: commonClass
} = CLASS_NAME

/**
 * Menu
 * The root element hamburger button,
 * and this controls menu and mobile search components
 */
export class Menu extends ComponentBase {
    private reduceMotion = false

    // HTML Elements
    private menu: HTMLElement
    private firstMenuItem: MenuItem
    private skipTo: Nullable<HTMLAnchorElement>
    private mobileSearch: HTMLInputElement

    /**
     * Attach all the event listeners
     * @param {HTMLButtonElement} hamburger
     */
    public constructor(public hamburger: HTMLButtonElement) {
        super()

        // Reducing motion will ignore the animation
        const motionReducedMQ = reduceMotionQueryList()
        this.handleReduceMotionChanged()
        // ignore TS deprecated here: addListener is part of MediaQueryList Interface
        // https://developer.mozilla.org/en-US/docs/Web/API/MediaQueryList
        if (motionReducedMQ.addListener) {
            motionReducedMQ.addListener(this.handleReduceMotionChanged)
        }

        // When menu initialization failed, shut down all functionalities.
        this.initMenu()
        if (!this.menu) {
            this.hamburger.remove()
            return
        }

        this.initMobileSearchForm()
        this.initMenuItems()

        this.skipTo = this.getElement<HTMLAnchorElement>(
            `.${commonClass.skipTo}`
        )

        // Hamburger button click: toggle menu
        this.hamburger.addEventListener(EventType.CLICK, () =>
            this.toggleMenu()
        )

        // Hamburger button key events
        this.hamburger.addEventListener(EventType.KEYDOWN, (e: KeyboardEvent) =>
            this.handleHamburgerKeyboardEvents(e)
        )

        // Document event Listeners to close the menu
        document.addEventListener(EventType.CLICK, (e: MouseEvent) =>
            this.attemptToClose(e.target)
        )

        HeaderStore.getInstance().menu = this
    }

    /**
     * Get the current menu open status
     * @returns {boolean}
     */
    private isMenuOpened(): boolean {
        return document.documentElement.classList.contains(menuClass.opened)
    }

    /**
     * Initialize menu
     */
    private initMenu(): void {
        const menu = this.getElement(`.${menuClass._}`)

        if (!menu) {
            return
        }

        this.menu = menu
    }

    /**
     * Initialize menu items
     * All items are linked by its keyboard events
     */
    private initMenuItems(): void {
        let prevItem: Nullable<MenuItem> = undefined
        let firstL2Item: Nullable<MenuItem> = undefined
        const rootItems: MenuItem[] = []

        // Per <li> elements
        this.getElements<HTMLLIElement>(`.${menuClass.item}`, this.menu).map(
            (item) => {
                const level = parseInt(
                    item.getAttribute(ATTR_NAME.header.menu.level) || '1'
                )
                const menuItem = new MenuItem(item, level)
                const prevL1 = rootItems[rootItems.length - 1]

                // All items can be navigated by a tab
                if (prevItem) {
                    prevItem.nextElement = menuItem
                    menuItem.prevElement = prevItem
                }

                // L1
                if (level === 1) {
                    // ⬅️ will be the L1 item even though it's L1 item (self)
                    menuItem.setArrowElement(menuItem, ArrowDirection.Left)
                    // ⬇️ and ⬆️ goes to the prev/next L1 item
                    if (prevL1) {
                        prevL1.setArrowElement(menuItem, ArrowDirection.Down)
                        menuItem.setArrowElement(prevL1, ArrowDirection.Up)
                    }

                    /**
                     * When the current is L1 and the first L2 exists, it means the prev item is the last L2,
                     * so connect the first L2 and the last L2 (prev item) with ⬇️ and ⬆️ to circle around
                     */
                    if (firstL2Item && prevItem) {
                        prevItem.setArrowElement(
                            firstL2Item,
                            ArrowDirection.Down
                        )
                        firstL2Item.setArrowElement(prevItem, ArrowDirection.Up)
                    }

                    rootItems.push(menuItem)
                    firstL2Item = undefined
                }

                // L2
                if (level === 2 && prevItem) {
                    // ⬅️ goes to the L1 item
                    menuItem.setArrowElement(prevL1, ArrowDirection.Left)

                    // This is the first L2 item, so prev item(L1)'s ➡️ is the current one
                    if (!firstL2Item) {
                        firstL2Item = menuItem
                        prevItem.setArrowElement(menuItem, ArrowDirection.Right)
                    } else {
                        // connect the current and prev item with ⬇️ and ⬆️
                        menuItem.setArrowElement(prevItem, ArrowDirection.Up)
                        prevItem.setArrowElement(menuItem, ArrowDirection.Down)
                    }
                }

                // L3
                // eslint-disable-next-line no-magic-numbers
                if (level === 3 && prevItem) {
                    // ⬅️ goes to the L1 item
                    menuItem.setArrowElement(prevL1, ArrowDirection.Left)
                    // connect the current and prev item with ⬇️ and ⬆️
                    menuItem.setArrowElement(prevItem, ArrowDirection.Up)
                    prevItem.setArrowElement(menuItem, ArrowDirection.Down)
                }

                prevItem = menuItem
            }
        )

        // Connect the first and last L1 with ⬇️ and ⬆️
        if (rootItems.length) {
            rootItems[rootItems.length - 1].setArrowElement(
                rootItems[0],
                ArrowDirection.Down
            )
            rootItems[0].setArrowElement(
                rootItems[rootItems.length - 1],
                ArrowDirection.Up
            )
        }

        // Link L1 items for resetting all HTML classes
        rootItems.forEach((item, index) => {
            if (rootItems[index + 1]) {
                item.nextRootItem = rootItems[index + 1]
            }
        })

        this.firstMenuItem = rootItems[0]
    }

    /**
     * Mobile search form
     * Tab goes to the menu, and shift + tab goes to hamburger
     */
    private initMobileSearchForm(): void {
        const mobileSearch = this.getElement<HTMLInputElement>(
            `.${searchClass._} input`,
            this.menu
        )

        if (!mobileSearch) {
            return
        }

        this.mobileSearch = mobileSearch
        this.mobileSearch.addEventListener(
            EventType.KEYDOWN,
            (e: KeyboardEvent) => {
                if (e.key !== KeyType.TAB) {
                    return
                }
                e.preventDefault()

                if (e.shiftKey) {
                    this.hamburger.focus()
                } else {
                    this.firstMenuItem.focus()
                }
            }
        )
    }

    /**
     * Reset the menu and menu item HTML classes
     */
    public removeRootItemClasses(): void {
        this.firstMenuItem.removeRootHTMLClasses()
    }

    /**
     * Reset the menu HTML classes
     * @param {string} className [optional] class name
     */
    public setMenuClass(className?: string): void {
        this.menu.classList.remove(
            menuClass.noColumn,
            menuClass.oneColumn,
            menuClass.twoColumn
        )

        if (!className) {
            return
        }
        this.menu.classList.add(className)
    }

    /**
     * Check where the user clicked
     * confirm against rules and return or close the menu
     * @param {Nullable<EventTarget>} target
     */
    private attemptToClose(target: Nullable<EventTarget>): void {
        if (
            !this.isMenuOpened() ||
            this.hamburger.contains(target as HTMLElement) ||
            this.menu.contains(target as HTMLElement)
        ) {
            return
        }

        // if you click outside the menu
        this.closeMenu()
    }

    /**
     * Handle when keyboard is used on the hamburger button
     * @param {KeyboardEvent} e
     */
    private handleHamburgerKeyboardEvents(e: KeyboardEvent): void {
        // When the menu opened, tab should focus the desktop search component
        if (
            e.key === KeyType.TAB &&
            !e.shiftKey &&
            !this.isMenuOpened() &&
            !isMobile()
        ) {
            e.preventDefault()
            HeaderStore.getInstance().search.focus()
            return
        }

        if (e.key === KeyType.ESC || e.key === KeyType.ESC_IE) {
            this.closeMenu()
            return
        }
    }

    /**
     * Check if menu is open or closed and call the opposite
     */
    private toggleMenu(): void {
        this.isMenuOpened() ? this.closeMenu() : this.openMenu()
    }

    /**
     * call all the logic for opening the menu
     */
    private openMenu(): void {
        if (CarComparisonStore.getInstance().vehicleFinder) {
            CarComparisonStore.getInstance().vehicleFinder.close()
        }
        document.documentElement.classList.remove(menuClass.closed)
        document.documentElement.classList.add(menuClass.opened)
        this.removeRootItemClasses()
        this.setMenuClass()

        this.openAnimationStart()

        // if reduce motion is true animation start/end events will not fire
        if (this.reduceMotion) {
            this.openAnimationEnd()
        }
    }

    /**
     * call all the logic for closing the menu
     */
    public closeMenu(): void {
        this.removeRootItemClasses()

        document.documentElement.classList.remove(menuClass.opened)
        document.documentElement.classList.add(menuClass.closed)

        this.closeAnimationStart()

        // if reduce motion is true animation start/end events will not fire
        if (this.reduceMotion) {
            this.closeAnimationEnd()
        }
    }

    /**
     * Moves focus to the first element in the menu
     * Tablet & Desktop: focus the first menu item
     * Mobile: focus the mobile search
     */
    private focusFirstItem(): void {
        if (!isMobile()) {
            this.firstMenuItem.focus()
            HeaderStore.getInstance().lastTouchedL1Item = this.firstMenuItem
            return
        }

        this.mobileSearch.focus()
    }

    /**
     * based on user preferences add or remove animation listeners
     */
    private handleReduceMotionChanged(): void {
        this.reduceMotion = isReduceMotion()

        if (this.reduceMotion) {
            this.removeAnimationEventListeners()
            return
        }

        this.addAnimationEventListeners()
    }

    /**
     * attach animation listeners for animation start and end
     */
    private addAnimationEventListeners(): void {
        document.addEventListener(
            EventType.ANIMATION_START,
            (e: AnimationEvent) => this.animationStartEventsHandler(e)
        )
        document.addEventListener(
            EventType.ANIMATION_END,
            (e: AnimationEvent) => this.animationEndEventsHandler(e)
        )
    }

    /**
     * remove animation listeners for animation start and end
     */
    private removeAnimationEventListeners(): void {
        document.removeEventListener(
            EventType.ANIMATION_START,
            (e: AnimationEvent) => this.animationStartEventsHandler(e)
        )
        document.removeEventListener(
            EventType.ANIMATION_END,
            (e: AnimationEvent) => this.animationEndEventsHandler(e)
        )
    }

    /**
     * filter animation by the name for the menu animation
     * call the functions that should be run when the animation starts
     * CSS dependency
     *
     * @param {AnimationEvent} e
     */
    private animationStartEventsHandler(e: AnimationEvent): void {
        if (e.animationName === AnimationName.SLIDE_UP) {
            this.closeAnimationStart()
        }
    }

    /**
     * filter animation by the name for the menu animation
     * call the functions that should be run when the animations end
     * CSS dependency
     *
     * @param {AnimationEvent} e
     */
    private animationEndEventsHandler(e: AnimationEvent): void {
        if (e.animationName === AnimationName.SLIDE_DOWN) {
            this.openAnimationEnd()
            return
        }

        if (e.animationName === AnimationName.SLIDE_UP) {
            this.closeAnimationEnd()
        }
    }

    /**
     * remove hidden attr from menu to show/animate it
     */
    private openAnimationStart(): void {
        this.menu.hidden = false
    }

    /**
     * disable tabbing on elements behind the menu once it has opened
     * toggle the aria values to inform the menu is open
     */
    private openAnimationEnd(): void {
        if (this.main) {
            this.main.tabIndex = -1
        }

        if (this.skipTo) {
            this.skipTo.tabIndex = -1
        }
        this.hamburger.setAttribute(AriaAttr.EXPANDED, 'true')
        this.hamburger.setAttribute(AriaAttr.LABEL, 'Close Main Menu')
        this.focusFirstItem()
    }

    /**
     * enable tabbing on elements behind the menu once it has closed
     * toggle the aria values to inform the menu is closed
     * return focus to the element that opened the menu
     * (fake natural focus order because elements aren't acutally next to each other)
     */
    private closeAnimationStart(): void {
        if (this.main) {
            this.main.removeAttribute(HtmlAttr.TAB_INDEX)
        }
        if (this.skipTo) {
            this.skipTo.removeAttribute(HtmlAttr.TAB_INDEX)
        }
        this.hamburger.setAttribute(AriaAttr.EXPANDED, 'false')
        this.hamburger.setAttribute(AriaAttr.LABEL, 'Open Main Menu')
        this.hamburger.focus()
    }

    /**
     * add hidden attr to menu to hide it
     */
    private closeAnimationEnd(): void {
        this.menu.hidden = true
    }
}
